import React from "react";
import {Upload as AntUpload} from "antd";
import {useTranslation} from "react-i18next";
// local
import {ButtonTransparent} from "./buttons/ButtonTransparent";
import {useUpload} from "helpers/uploadHooks";
import {LockValue} from "components/LockValue";
import UploadedFilesList from "./UploadedFilesList";
import {ConfirmModal} from "components/ConfirmModal";
import {IconPaperClip} from "components/icons";
import cn from "classnames";

// iconButton, buttonFirst and smallButton style the upload button,
// the rest are props of UploadedFilesList (buttonView types: "cross", "delete")
const commonVariantProps = {
  rowView: true,
  smallButton: true,
  buttonView: "delete",
};
const agreement = {...commonVariantProps, isIconDownload: true};

// files, uploadRequest, removeRequest, onChange, disabledUpload, customFileTypes, customFileSize,
// multiple and maxFilesCount configure useUpload. To share the list with a drop area, call
// useUpload outside and pass its result as `upload`, then those props are not needed
const Upload = ({
  files,
  variant = "form",
  viewMode,
  uploadRequest,
  removeRequest,
  onChange,
  disabledUpload,
  needRemoveConfirm = true,
  customFileTypes,
  customFileSize,
  multiple,
  maxFilesCount,
  upload,
  uploadPermissions,
  wrapperClass,
  uploadButtonClass,
  filenameMaxViewLength = {
    view: 50,
    edit: 20,
  },
  fileListClass,
}) => {
  const {t} = useTranslation();

  const variants = {
    form: {listBorder: false},
    agreement,
    agreementCompact: {...agreement, miniLoadFiles: true},
    overview: {isIconDownload: true, miniLoadFiles: true, listBorder: false},
    attachment: {
      ...commonVariantProps,
      iconButton: true,
      buttonFirst: true,
      miniLoadFiles: true,
      listBorder: false,
      fileBorder: false,
    },
  };

  const ownUpload = useUpload({
    files,
    onChange,
    uploadRequest,
    removeRequest,
    disabledUpload,
    customFileTypes,
    customFileSize,
    multiple,
    maxFilesCount,
  });
  const currentUpload = upload ?? ownUpload;
  const {loading, isUploadDisabled, removeFile, uploadProps} = currentUpload;
  const visibleFiles = currentUpload.files.filter(Boolean);
  const {iconButton, buttonFirst, smallButton, ...listProps} = variants[variant];

  const onRemove = (index) => {
    const file = visibleFiles[index];
    if (!needRemoveConfirm) return removeFile(file);
    ConfirmModal({
      title: t("WANT_DELETE_FILE"),
      text: t("CANT_DELETE_FILE"),
      type: "warning",
      onOk: () => removeFile(file),
    });
  };

  const fileListProps = {
    listBorder: true,
    fileBorder: true,
    ...listProps,
    files: visibleFiles,
    viewMode,
    removeFile: onRemove,
    filenameMaxViewLength,
    wrapperClass: fileListClass,
  };

  return (
    <div
      className={cn("editBlock_upload__item", {
        "__row-view": listProps.rowView,
        [wrapperClass]: !!wrapperClass,
      })}
    >
      {!buttonFirst && <UploadedFilesList {...fileListProps} />}
      {!viewMode && (
        <AntUpload {...uploadProps}>
          <LockValue
            canPermissions={uploadPermissions}
            needLabel
            needShowModalToSubscribe
            noLockIcon
            dimmedWhenLocked
            addClassIcon="mb-1 ml-0"
          >
            <ButtonTransparent
              name={iconButton ? undefined : loading ? "LOADING" : "UPLOAD"}
              addClass={cn("editBlock_upload__btn", {
                "mb-2": listProps.rowView,
                "pl-0 pr-0": iconButton,
                [uploadButtonClass]: !!uploadButtonClass,
              })}
              icon={iconButton ? <IconPaperClip /> : undefined}
              noBorder={iconButton}
              circular={iconButton}
              loading={loading}
              disabled={isUploadDisabled}
              small={smallButton}
              clickable
              nowrap
            />
          </LockValue>
        </AntUpload>
      )}
      {buttonFirst && <UploadedFilesList {...fileListProps} />}
    </div>
  );
};

export default Upload;
